import Swal from "sweetalert2";

/**
 * Handles the DUPLICATE_UPC error returned when a pitch is saved with a UPC
 * that is already assigned to another pitch.
 *
 * The backend gates disclosure with `canView`: pitches inside the user's own
 * company come back fully populated, pitches belonging to another company come
 * back as `conflictingPitch: null` so no release information ever leaks.
 */

export interface ConflictingPitch {
  id: number;
  releaseTitle: string;
  artistName: string;
  configuration: string | null;
  status: string;
  deliveredDSPs: string[];
  deliveredAt: string | null;
  deliveredAtFormatted: string | null;
}

export interface UpcConflictResponse {
  code: string;
  message?: string;
  releaseUPC?: string;
  canView: boolean;
  conflictingPitch: ConflictingPitch | null;
}

const SUPPORT_EMAIL = "admin@cadenceio.com";

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/**
 * Narrows an arbitrary axios error to a UPC conflict payload.
 * Returns null when the error is anything else, so callers can fall through
 * to their existing generic error handling.
 */
export const getUpcConflict = (error: any): UpcConflictResponse | null => {
  const data = error?.response?.data;
  if (data?.code !== "DUPLICATE_UPC") return null;
  return data as UpcConflictResponse;
};

/**
 * A submitted or delivered pitch is read-only, so send the user to the
 * submission view. Drafts open directly in the edit form.
 */
const getPitchUrl = (pitch: ConflictingPitch): string =>
  pitch.status?.toLowerCase().includes("draft")
    ? `/pitch/edit/${pitch.id}`
    : `/pitch/submission/${pitch.id}?from=dashboard`;

const buildConflictHtml = (
  conflict: UpcConflictResponse,
  pitch: ConflictingPitch,
): string => {
  const rows: string[] = [
    `<div><strong>${escapeHtml(pitch.releaseTitle || "Untitled release")}</strong></div>`,
    `<div>${escapeHtml(pitch.artistName || "Unknown artist")}</div>`,
  ];

  if (pitch.configuration) {
    rows.push(`<div>${escapeHtml(pitch.configuration)}</div>`);
  }

  // "Delivered" is not a status value — it is derived from the DSP list.
  const isDelivered = (pitch.deliveredDSPs?.length ?? 0) > 0;
  if (isDelivered) {
    const dsps = pitch.deliveredDSPs.map(escapeHtml).join(", ");
    const deliveredOn = pitch.deliveredAtFormatted
      ? ` on ${escapeHtml(pitch.deliveredAtFormatted)}`
      : "";
    rows.push(`<div>Status: Delivered to ${dsps}${deliveredOn}</div>`);
  } else {
    rows.push(`<div>Status: ${escapeHtml(pitch.status || "Unknown")}</div>`);
  }

  const upcLine = conflict.releaseUPC
    ? `<p style="margin-bottom:12px">UPC <strong>${escapeHtml(conflict.releaseUPC)}</strong> is already used on another pitch.</p>`
    : `<p style="margin-bottom:12px">This UPC is already used on another pitch.</p>`;

  return `
    ${upcLine}
    <div class="p-3 mb-3 text-left border rounded-card bg-neutral-50 border-neutral-200">
      ${rows.join("")}
    </div>
    <p style="text-align:left;margin-bottom:8px">
      If the UPC on that pitch is wrong, open it and correct the UPC there.
      This UPC will then be free to use.
    </p>
    <p class="text-sm text-left text-neutral-500">
      If the problem persists, please contact support at ${SUPPORT_EMAIL}.
    </p>
  `;
};

/**
 * Renders the UPC conflict dialog.
 *
 * @param navigate react-router navigate function, used by the "View this pitch" action.
 * @returns a promise that resolves once the dialog is dismissed.
 */
export const showUpcConflictAlert = async (
  conflict: UpcConflictResponse,
  navigate: (path: string) => void,
): Promise<void> => {
  const pitch = conflict.conflictingPitch;

  // Different company — show nothing identifying, no link.
  if (!conflict.canView || !pitch) {
    await Swal.fire({
      title: "UPC Unavailable",
      text:
        conflict.message ||
        `This UPC is already in use and cannot be assigned. Please contact support at ${SUPPORT_EMAIL}.`,
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  const result = await Swal.fire({
    title: "UPC Already In Use",
    html: buildConflictHtml(conflict, pitch),
    icon: "error",
    showCancelButton: true,
    confirmButtonText: "View this pitch",
    cancelButtonText: "Close",
  });

  if (result.isConfirmed) {
    navigate(getPitchUrl(pitch));
  }
};

/**
 * Convenience wrapper: shows the conflict dialog when the error is a UPC
 * conflict and reports whether it handled the error.
 */
export const handleUpcConflict = async (
  error: any,
  navigate: (path: string) => void,
): Promise<boolean> => {
  const conflict = getUpcConflict(error);
  if (!conflict) return false;
  await showUpcConflictAlert(conflict, navigate);
  return true;
};
